import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, type PairingArchive } from "../api";
import { BookCover } from "../components/BookCover";
import { Card, EmptyState, SectionHeading, Spinner } from "../components/ui";
import { NotebookNav } from "./Quotes";

type Tab = "drinks" | "food";

export default function RecipesPage() {
  const [tab, setTab] = useState<Tab>("drinks");
  const list = useQuery({
    queryKey: ["recipes"],
    queryFn: () => api.get<{ pairings: PairingArchive[] }>("/recipes"),
  });

  const pairings = list.data?.pairings ?? [];

  return (
    <div className="flex flex-col gap-10">
      <NotebookNav />

      <SectionHeading
        eyebrow="The hosting archive"
        title="Recipes"
        description="Every pairing the club has generated, in one place. Use them again the next time someone hosts."
      />

      <div className="flex items-center gap-2">
        <FilterPill label="In the glass" active={tab === "drinks"} onClick={() => setTab("drinks")} />
        <FilterPill label="On the table" active={tab === "food"} onClick={() => setTab("food")} />
      </div>

      {list.isLoading ? (
        <Spinner />
      ) : pairings.length === 0 ? (
        <Card accent>
          <EmptyState
            title="No pairings yet"
            description={`Generate "Food & drink pairings" on any book's page and they'll collect here.`}
          />
        </Card>
      ) : (
        <div className="flex flex-col gap-7">
          {pairings.map((p) => (
            <Card key={p.book.id} accent>
              <div className="flex flex-col md:flex-row gap-6">
                <Link to={`/book/${p.book.id}`} className="shrink-0">
                  <BookCover url={p.book.coverUrl} title={p.book.title} author={p.book.author} size="md" />
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/book/${p.book.id}`}>
                    <h3 className="font-serif text-2xl text-plum hover:text-rose">{p.book.title}</h3>
                  </Link>
                  <p className="text-plum-soft italic text-sm">by {p.book.author}</p>

                  <div className="mt-5">
                    {tab === "drinks" ? (
                      <ul className="flex flex-col gap-5">
                        {p.drinks.map((d, i) => (
                          <li key={i} className="flex flex-col gap-1.5">
                            <div className="font-serif text-base text-plum">{d.name}</div>
                            <p className="text-plum-soft text-sm leading-relaxed">{d.description}</p>
                            <p className="mt-1 px-3 py-2 rounded-lg bg-blush-soft/40 text-plum-light text-xs leading-relaxed font-mono">
                              {d.recipe}
                            </p>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <ul className="flex flex-col gap-4">
                        {p.food.map((f, i) => (
                          <li key={i}>
                            <div className="font-serif text-base text-plum">{f.name}</div>
                            <p className="text-plum-soft text-sm leading-relaxed mt-0.5">{f.description}</p>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function FilterPill({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={[
        "px-3 py-1 rounded-full text-xs uppercase tracking-[0.14em] transition-colors",
        active ? "bg-rose text-white" : "bg-cream-100 text-plum-soft hover:bg-cream-200",
      ].join(" ")}
    >
      {label}
    </button>
  );
}
